import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getAllBranding } from "@/lib/api";

const DEFAULT_LOGO = `${process.env.PUBLIC_URL}/hospital-logo-80.png`;
const DEFAULT_LOGO_SMALL = `${process.env.PUBLIC_URL}/hospital-logo-40.png`;
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL}/icon.svg`;
const DEFAULT_LOGIN_BG = `${process.env.PUBLIC_URL}/facade.jpg`;

const BrandingContext = createContext({
  logoUrl: DEFAULT_LOGO,
  logoSmallUrl: DEFAULT_LOGO_SMALL,
  faviconUrl: DEFAULT_FAVICON,
  loginBgUrl: DEFAULT_LOGIN_BG,
  canonicalUrl: null,
  refresh: () => {},
});

function applyFavicon(url, contentType) {
  if (!url) return;
  // Remove existing icon links and replace with our dynamic one
  const existing = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']");
  existing.forEach(l => l.parentNode?.removeChild(l));
  const link = document.createElement("link");
  link.rel = "icon";
  if (contentType) link.type = contentType;
  link.href = url;
  document.head.appendChild(link);
}

export function BrandingProvider({ children }) {
  const [state, setState] = useState({
    logoUrl: DEFAULT_LOGO,
    logoSmallUrl: DEFAULT_LOGO_SMALL,
    faviconUrl: DEFAULT_FAVICON,
    loginBgUrl: DEFAULT_LOGIN_BG,
    canonicalUrl: null,
  });

  const refresh = useCallback(async () => {
    try {
      const [brandRes, cfgRes] = await Promise.all([
        getAllBranding(),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/config`).catch(() => ({ data: {} })),
      ]);
      const d = brandRes.data || {};
      const cfg = cfgRes.data || {};
      const customLogo = d.logo?.image_data_url || null;
      const customFavicon = d.favicon?.image_data_url || null;
      const customLoginBg = d.login_bg?.image_data_url || null;

      setState({
        logoUrl: customLogo || DEFAULT_LOGO,
        logoSmallUrl: customLogo || DEFAULT_LOGO_SMALL,
        faviconUrl: customFavicon || DEFAULT_FAVICON,
        loginBgUrl: customLoginBg || DEFAULT_LOGIN_BG,
        canonicalUrl: cfg.canonical_url || null,
      });

      // Apply favicon to DOM (browser tab)
      if (customFavicon) {
        applyFavicon(customFavicon, d.favicon?.content_type);
      } else {
        applyFavicon(DEFAULT_FAVICON, "image/svg+xml");
      }

      // If a canonical URL is set and the user is on a different host, redirect
      // them once so all employees converge on the single official environment.
      if (cfg.canonical_url) {
        try {
          const canon = new URL(cfg.canonical_url);
          if (window.location.host !== canon.host) {
            const target = `${canon.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
            console.warn(`[Branding] Redirecting to canonical host ${canon.host}`);
            window.location.replace(target);
          }
        } catch {
          /* ignore malformed canonical_url */
        }
      }
    } catch {
      // Network error — keep defaults
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <BrandingContext.Provider value={{ ...state, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
